import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import Swal from 'sweetalert2';

import { EventType } from '@app/shared/constants-server';
import { businessDayOf } from '@app/shared/time';
import type { RentalInterruption } from '@app/shared/_models/sales';
import type { Day, RentalLineView } from '../rental-billing.model';
import { RentalBillingService, SimulationRequest, SimulationState, simulationStream, tooManyDecisions } from '../rental-billing.service';
import { CLOSED_LINE, closureTip, lineClosed, patientLabel, rentalSubtitle, rentalTitle, statusLabel } from '../rental-billing.labels';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import { RentalDecisionEvent, decisionCancel, interruptionAdd, interruptionReplace } from '../rental-decision-events';
import type { DecisionDialogResult, RangeDialogData, RangeDialogOrigin, RangeEditTarget } from '../rental-dialogs.contract';
import { addDays, addMonthsClamped, firstOfMonth, frDay, frShort, maxDay, minDay } from '../rental-days';
import { PickerMarks, lockedDaysIn, parseTypedDay, pickerMarksOf, rangeText } from '../rental-picker.geometry';
import { deliveredDay, memoGroupsOf, returnedDay, sameDecisionKey, sameMemoLineIds } from '../rental-planning.geometry';
import { PickerPhase, RentalDayPickerComponent } from '../day-picker/rental-day-picker.component';
import { RentalEffectComponent } from '../effect/rental-effect.component';
import { simulationEvents } from '../decisions/rental-decision-dialog.base';

type Quick = 'today' | 'yesterday' | 'delivery' | 'unknown' | 'month' | number;
interface LineVm { id: number; name: string; sub: string; checked: boolean; disabled: boolean; title: string | null; }
interface GroupVm { key: string; label: string; lines: LineVm[]; all: boolean; some: boolean; disabled: boolean; }
interface ReasonVm { id: number; name: string; color: string; pressed: boolean; }
interface OverlapVm { text: string; entries: RangeEditTarget[]; sample: RentalInterruption; canExtend: boolean; }
interface RangeVm {
  ready: boolean; title: string; asOf: Day; floor: Day | null; groups: GroupVm[]; allChecked: boolean; checkedIds: number[];
  reasons: ReasonVm[]; reasonsNote: string | null; commentHint: string; startText: string; endText: string; marks: PickerMarks | null;
  rangeText: string; scope: string | null; warnings: string[]; errors: string[]; overlap: OverlapVm | null;
  canSave: boolean; resumeToday: 'show' | 'started-today' | 'not-started' | null;
  simulable: boolean; // seuls les textes manquent (commentaire, motif de la modification) : l'effet se chiffre déjà
}

// Origines « rangée de matériel » : la ligne et ses voisines de même mémo (décision de JP) ; la fiche ne coche que sa ligne.
const SAME_MEMO_ORIGINS: ReadonlySet<RangeDialogOrigin> = new Set<RangeDialogOrigin>(['row', 'drag', 'click', 'menu']);
const FAR = '9999-12-31';

function selectable(l: RentalLineView | undefined): boolean {
  return !!l && l.id > 0 && l.typ === 'RENTAL' && !l.lifecycle_unreadable && l.decisions != null && !lineClosed(l);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

// Plusieurs mémos (établissement) : « Pour » à gauche, une rangée par chambre, saisie à droite. Largeur d'après la chambre la plus garnie :
// marges 48 + mémo 140 + écart 14 + n × matériel 260 + (n − 1) × écart 10 + écart 24 + saisie 884 = calendrier de trois mois (cf. .wide dans le scss).
export interface RangeScopeLayout { wide: boolean; cols: number; width: string; }
export function rangeScopeLayout(lines: RentalLineView[]): RangeScopeLayout {
  const groups = memoGroupsOf(lines);
  const cols = Math.max(1, ...groups.map((g) => g.lines.length));
  const wide = groups.length > 1;
  return { wide, cols, width: wide ? `min(${1100 + cols * 270}px, 95vw)` : '960px' };
}

// Interruption (2102) sur les lignes cochées : un évènement par ligne (E.5). Plage du start au end, les DEUX inclus :
// start = premier jour non facturé (null = dès la livraison), end = dernier jour non facturé (null = interruption ouverte).
// Le geste commercial est une interruption comme une autre : il se saisit ici en choisissant le motif offert (data.offered, motif 12),
// qui donne au dialogue le titre et le violet de l'ancien geste (2104, supprimé le 27/10/2026).
@Component({
  selector: 'app-rental-range-dialog',
  templateUrl: './rental-range-dialog.component.html',
  styleUrls: ['./rental-range-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule, RentalDayPickerComponent, RentalEffectComponent],
})
export class RentalRangeDialogComponent {
  readonly data: RangeDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<RentalRangeDialogComponent, DecisionDialogResult> = inject(MatDialogRef);
  private svc = inject(RentalBillingService);
  private dialogs = inject(RentalDecisionDialogsService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @ViewChild('editReasonInput') editReasonInput?: ElementRef<HTMLInputElement>;
  @ViewChild('pickBox') pickBox?: ElementRef<HTMLElement>;

  readonly store = this.data.store;
  readonly layout = rangeScopeLayout(this.store.rentalLines());
  checked = new Set<number>();
  reasonId: number | null = null;
  comment = '';
  start: Day | null | undefined = undefined; // premier jour non facturé ; undefined = vide ; null = « Dès la livraison »
  end: Day | null = null; // DERNIER jour non facturé, inclus ; null = interruption ouverte (reprise inconnue)
  phase: PickerPhase = 'start';
  readonly months = 3 as const; // mois du jour du calcul au milieu, un avant, un après (JP 30/10)
  leftMonth: Day = '';
  editReason = '';
  edit: RangeEditTarget[] | null = null;
  saving = false;
  sim: SimulationState = { loading: false, view: null, error: null };
  vm: RangeVm = this.emptyVm();

  private original: RentalInterruption | null = null;
  private initialised = false;
  private marksKey = '';
  private marks: PickerMarks | null = null;
  private events$ = new BehaviorSubject<SimulationRequest | null>(null);

  constructor() {
    this.store.ensureReasons();
    simulationStream(this.svc, this.data.orderId, this.events$, this.data.store.openOnly)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => {
        this.sim = s;
        this.cdr.markForCheck();
      });
    this.store.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refresh();
      this.cdr.markForCheck();
    });
    this.dialogRef.keydownEvents?.()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ev) => {
        if (ev.key === 'Escape') this.onEscape(ev);
      });
    this.refresh();
  }

  get isEdit(): boolean {
    return !!this.edit?.length;
  }

  // Motif choisi aux jours offerts (geste commercial) : titre, libellés et violet de l'ancien geste.
  get offered(): boolean {
    return this.reasonId !== null && this.store.reasonOffered(this.reasonId);
  }

  toggleLine(id: number): void {
    if (this.checked.has(id)) this.checked.delete(id);
    else this.checked.add(id);
    this.refresh();
  }

  toggleGroup(g: GroupVm): void {
    const ids = g.lines.filter((l) => !l.disabled).map((l) => l.id);
    if (g.all) ids.forEach((id) => this.checked.delete(id));
    else ids.forEach((id) => this.checked.add(id));
    this.refresh();
  }

  toggleAll(): void {
    const ids = this.vm.groups.flatMap((g) => g.lines.filter((l) => !l.disabled).map((l) => l.id));
    if (this.vm.allChecked) {
      this.checked.clear();
      this.refresh();
      return;
    }
    const checkAll = () => {
      ids.forEach((id) => this.checked.add(id));
      this.refresh();
      this.cdr.markForCheck();
    };
    if (this.vm.groups.length < 2) {
      checkAll();
      return;
    }
    Swal.fire({
      title: `Cocher les ${this.vm.groups.length} résidents ?`,
      text: 'Une décision sera enregistrée sur chaque matériel coché.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Tout cocher',
      cancelButtonText: 'Annuler',
    }).then((r) => {
      if (r.isConfirmed) checkAll();
    });
  }

  pickReason(id: number): void {
    const first = this.start === undefined;
    this.reasonId = id;
    if (first && this.store.reasonFlag(id, 'from_delivery')) {
      this.start = null;
      this.end = null;
      this.phase = 'end';
      this.showMonthOf(this.floor());
    }
    this.refresh();
    // Motif choisi avant les dates : le calendrier vient à l'écran sans geste de plus (scène A en 5 clics à 1 536 × 864).
    if (first) setTimeout(() => this.pickBox?.nativeElement.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }));
  }

  onComment(v: string): void {
    this.comment = v;
    this.refresh();
  }

  onEditReason(v: string): void {
    this.editReason = v;
    this.refresh();
  }

  onFieldFocus(which: 'start' | 'end'): void {
    const p: PickerPhase = which === 'end' && this.start !== undefined ? 'end' : 'start';
    if (p === this.phase) return;
    this.phase = p;
    this.refresh();
  }

  // Même analyseur que les autres dialogues (parseTypedDay) : « 28 » = le 28 du mois affiché à gauche ; réécrite JJ/MM/AAAA à la sortie du champ.
  onTyped(which: 'start' | 'end', input: HTMLInputElement): void {
    const v = parseTypedDay(input.value, this.leftMonth.slice(0, 7));
    if (v === undefined) {
      input.setCustomValidity('Date non comprise');
      input.reportValidity();
      return;
    }
    input.setCustomValidity('');
    if (which === 'start') {
      this.start = v === null ? undefined : v;
      if (v) this.showMonthOf(v);
      this.phase = this.start !== undefined && this.end === null ? 'end' : 'start';
    } else {
      this.end = v;
      this.phase = 'start';
    }
    this.refresh();
    input.value = which === 'start' ? this.vm.startText : this.vm.endText;
  }

  // +k j = k jours interrompus, le premier compris (dernier jour = début + k − 1) ; +1 mois = jusqu'à la veille du même quantième le mois suivant.
  quick(k: Quick): void {
    const asOf = this.vm.asOf;
    if (k === 'today' || k === 'yesterday') {
      this.start = k === 'today' ? asOf : addDays(asOf, -1);
      this.end = null;
      this.phase = 'end';
      this.showMonthOf(this.start);
    } else if (k === 'delivery') {
      this.start = null;
      this.end = null;
      this.phase = 'end';
      this.showMonthOf(this.floor());
    } else if (k === 'unknown') {
      if (this.start === undefined) return;
      this.end = null;
      this.phase = 'start';
    } else {
      const base = this.startEff();
      if (!base) return;
      this.end = k === 'month' ? addDays(addMonthsClamped(base, 1), -1) : addDays(base, k - 1);
      this.phase = 'start';
    }
    this.refresh();
  }

  onPickStart(d: Day | null): void {
    this.start = d ?? undefined; // null du calendrier = sélection effacée (Échap)
    this.refresh();
  }

  onPickEnd(d: Day | null): void {
    this.end = d;
    this.refresh();
  }

  onPhase(p: PickerPhase): void {
    this.phase = p;
    this.refresh();
  }

  onLeftMonth(first: Day): void {
    this.leftMonth = first;
  }

  // Chevauchement de même motif sur les mêmes lignes : on passe en modification de l'entrée existante, sur l'union des deux plages.
  extend(ov: OverlapVm): void {
    const s = ov.sample;
    const start = this.start === undefined ? s.start : s.start === null || this.start === null ? null : minDay(s.start, this.start);
    const end = s.end === null || this.end === null ? null : maxDay(s.end, this.end);
    this.edit = ov.entries.map((e) => ({ ...e }));
    this.original = s;
    this.start = start;
    this.end = end;
    if (!this.comment.trim()) this.comment = s.comment ?? '';
    this.phase = 'start';
    this.refresh();
    setTimeout(() => this.editReasonInput?.nativeElement.focus());
  }

  // Reprise au jour du calcul : l'entrée ouverte est remplacée, son dernier jour interrompu devient la veille d'as_of ; le motif de la modification reste exigé (I.3).
  resumeToday(): void {
    this.end = addDays(this.vm.asOf, -1);
    this.phase = 'start';
    this.refresh();
    if (this.vm.canSave) this.save();
    else this.editReasonInput?.nativeElement.focus();
  }

  cancelDecision(): void {
    const edit = this.edit ?? [];
    const o = this.original;
    if (!edit.length || !o) return;
    const range = rangeText(o.start, o.end, minDay(...edit.map((e) => businessDayOf(this.store.line(e.lineId)?.realdatdel)))).text;
    const targets = edit.map((e) => ({ ...e, checked: this.checked.has(e.lineId) }));
    const offered = this.store.reasonOffered(o.reason_id);
    this.dialogRef.close({ saved: 0, failed: false });
    this.dialogs.openCancelDecision({
      store: this.store, orderId: this.data.orderId, typ: EventType.RENTAL_INTERRUPTION, targets,
      title: offered ? 'Annuler le geste commercial' : "Annuler l'interruption",
      summary: `${this.store.reasonName(o.reason_id)} · ${range}`,
    });
  }

  save(): void {
    const evs = this.buildEvents();
    if (!evs || !this.vm.canSave || this.saving) return;
    this.saving = true;
    this.cdr.markForCheck();
    // Pas de takeUntilDestroyed : une série commencée va jusqu'à son bilan.
    const offered = this.offered;
    this.svc.postDecisions(evs).subscribe((run) => {
      const res = offered ? this.dialogs.settle(this.store, run, 'Geste commercial', 'm') : this.dialogs.settle(this.store, run, 'Interruption');
      this.saving = false;
      this.dialogRef.close(res);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close({ saved: 0, failed: false });
  }

  // Échap dans un champ texte n'efface rien ; en modification, les dates d'origine ne s'effacent jamais : Échap ferme, comme [Fermer].
  private onEscape(ev: KeyboardEvent): void {
    if (this.saving || Swal.isVisible()) return;
    ev.preventDefault();
    const t = ev.target as HTMLElement | null;
    if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA') return;
    if (!this.isEdit && (this.start !== undefined || this.end !== null)) {
      this.start = undefined;
      this.end = null;
      this.phase = 'start';
      this.refresh();
      this.cdr.markForCheck();
    } else {
      this.close();
    }
  }

  private init(): boolean {
    const v = this.store.view;
    if (!v) return false;
    const lines = this.store.rentalLines();
    const byId = new Map(lines.map((l) => [l.id, l]));
    let ids = (this.data.lineIds ?? []).filter((id) => byId.has(id));
    if (SAME_MEMO_ORIGINS.has(this.data.origin)) ids = [...new Set(ids.flatMap((id) => sameMemoLineIds(byId.get(id)!, lines)))];
    this.edit = this.data.edit?.length ? this.data.edit.map((e) => ({ ...e })) : null;
    const first = this.edit?.[0];
    const carrier = first ? byId.get(first.lineId) : undefined;
    this.original = first ? (carrier?.decisions?.interruptions.find((i) => i.id === first.id) ?? null) : null;
    this.checked = new Set(ids.filter((id) => selectable(byId.get(id))));
    const o = this.original;
    if (o) {
      this.reasonId = o.reason_id;
      this.comment = o.comment ?? '';
      this.start = o.start;
      this.end = o.end;
    }
    if (this.data.start !== undefined) this.start = this.data.start;
    if (this.data.end !== undefined) this.end = this.data.end ?? null;
    this.phase = this.start !== undefined && this.end === null && !this.isEdit ? 'end' : 'start';
    const ref = typeof this.start === 'string' ? this.start : this.start === null ? this.floor() : null;
    this.leftMonth = addMonthsClamped(firstOfMonth(v.as_of), -1); // une plage préremplie hors de ces trois mois est montrée quand même
    this.showMonthOf(ref);
    return true;
  }

  private refresh(): void {
    if (!this.initialised) this.initialised = this.init();
    this.vm = this.initialised ? this.buildVm() : this.emptyVm();
    // Textes ôtés, motif d'annulation fictif : la saisie d'un texte ne relance rien ; un dossier rechargé (computed_at) rechiffre la même saisie.
    this.events$.next(this.vm.ready ? { events: simulationEvents(this.buildEvents()), at: this.store.view?.computed_at ?? null } : null);
  }

  private emptyVm(): RangeVm {
    return {
      ready: false, title: this.titleText(), asOf: '', floor: null, groups: [], allChecked: false, checkedIds: [], reasons: [], reasonsNote: null,
      commentHint: 'Commentaire (facultatif)', startText: '', endText: '', marks: null, rangeText: '', scope: null, warnings: [], errors: [],
      overlap: null, canSave: false, resumeToday: null, simulable: false,
    };
  }

  private titleText(): string {
    if (this.offered) return this.isEdit ? 'Geste commercial' : 'Nouveau geste commercial';
    return this.isEdit ? "Modifier l'interruption" : 'Nouvelle interruption';
  }

  private buildVm(): RangeVm {
    const v = this.store.view!;
    const lines = this.store.rentalLines();
    const byId = new Map(lines.map((l) => [l.id, l]));
    for (const id of [...this.checked]) if (!selectable(byId.get(id))) this.checked.delete(id);
    const checkedLines = lines.filter((l) => this.checked.has(l.id));
    const checkedIds = checkedLines.map((l) => l.id);
    const groups: GroupVm[] = memoGroupsOf(lines).map((g) => {
      const lv = g.lines.map((l) => this.lineVm(l));
      const active = lv.filter((l) => !l.disabled);
      const n = active.filter((l) => l.checked).length;
      return {
        key: g.key, label: `${patientLabel(g.memo, this.store.beneficiary)} (${g.lines.length})`, lines: lv,
        all: active.length > 0 && n === active.length, some: n > 0, disabled: active.length === 0,
      };
    });
    const allActive = groups.flatMap((g) => g.lines.filter((l) => !l.disabled));
    const floor = this.floor();
    const marks = this.marksFor(checkedLines);
    // Motifs d'usage INTERRUPTION, gestes commerciaux compris (data.offered = true, pastille violette par leur couleur c_values).
    const reasonList = this.store.reasonsFor('INTERRUPTION').map((r) => ({ id: r.id!, name: r.name ?? `motif ${r.id}` }));
    if (this.reasonId !== null && !reasonList.some((r) => r.id === this.reasonId)) reasonList.push({ id: this.reasonId, name: this.store.reasonName(this.reasonId) });
    const reasons = reasonList.map((r) => ({ ...r, color: this.store.reasonColor(r.id) ?? (this.store.reasonOffered(r.id) ? 'var(--violet)' : 'var(--amber)'), pressed: r.id === this.reasonId }));
    const commentRequired = this.reasonId !== null && this.store.reasonFlag(this.reasonId, 'comment_required');
    const startEff = this.startEff();

    const errors: string[] = [];
    if (!checkedIds.length) errors.push('Aucun matériel coché.');
    if (startEff && this.end !== null && this.end < startEff) errors.push(this.offered ? 'Le dernier jour offert ne peut précéder le début.' : 'Le dernier jour interrompu ne peut précéder le début.');
    const plan = this.editPlan(checkedIds);
    const many = tooManyDecisions(plan ? plan.replace.length + plan.add.length + plan.cancel.length : checkedIds.length);
    if (many) errors.push(many);
    const simulable = !errors.length;
    if (commentRequired && !this.comment.trim()) errors.push('Commentaire obligatoire pour ce motif.');
    if (plan && (plan.replace.length || plan.cancel.length) && !this.editReason.trim()) errors.push('Motif de la modification obligatoire.');

    const warnings: string[] = [];
    const notice = v.is_fse && this.reasonId !== null ? this.store.reasonNotice(this.reasonId) : null;
    if (notice) warnings.push(notice);
    if (marks && startEff) {
      const n = lockedDaysIn(marks, startEff, this.end);
      if (n > 0) warnings.push(`${plural(n, 'jour déjà facturé', 'jours déjà facturés')} dans la plage`);
    }

    const text = this.start === undefined
      ? "Cliquez le premier jour d'interruption puis, si vous le connaissez, le dernier jour interrompu (inclus)."
      : rangeText(this.start, this.end, floor).text;
    const names = checkedLines.map((l) => l.product_name ?? `article ${l.product}`);
    const overlap = this.overlapOf(lines, checkedIds);
    const changes = !plan || plan.replace.length + plan.add.length + plan.cancel.length > 0;
    const canSave = changes && !this.saving && !errors.length && !!this.reasonId && this.start !== undefined;
    const o = this.original;
    let resumeToday: RangeVm['resumeToday'] = null;
    if (this.isEdit && o && o.end === null) {
      // Dernier jour interrompu = veille d'as_of : refusé si le début effectif est as_of, le serveur exigeant end ≥ start.
      const eff = o.start ?? floor;
      resumeToday = eff === null || eff < v.as_of ? 'show' : eff === v.as_of ? 'started-today' : 'not-started';
    }

    return {
      ready: true, title: this.titleText(), asOf: v.as_of, floor, groups, allChecked: allActive.length > 0 && allActive.every((l) => l.checked), checkedIds,
      reasons, reasonsNote: this.reasonsNote(reasons.length), commentHint: commentRequired ? 'Commentaire (obligatoire)' : 'Commentaire (facultatif)',
      startText: this.start === undefined ? '' : this.start === null ? (floor ? `Dès la livraison (${frShort(floor)})` : 'Dès la livraison') : frDay(this.start),
      endText: this.end ? frDay(this.end) : '', marks, rangeText: text, scope: names.length ? '· ' + names.join(', ') : null,
      warnings, errors, overlap, canSave, resumeToday, simulable,
    };
  }

  private lineVm(l: RentalLineView): LineVm {
    const name = rentalTitle(l) ?? `article ${l.product}`;
    if (lineClosed(l)) return { id: l.id, name, sub: CLOSED_LINE, checked: false, disabled: true, title: closureTip(l.closure!) };
    if (!selectable(l)) {
      const why = l.anomalies?.[0] ?? 'cycle de vie illisible';
      return { id: l.id, name, sub: `illisible : ${why}`, checked: false, disabled: true, title: why };
    }
    const sub = [rentalSubtitle(l), l.billed_until ? `facturé jusqu'au ${frShort(l.billed_until)}` : 'rien de facturé', l.return_realdatdel ? `repris le ${frShort(returnedDay(l))}` : null, statusLabel(l.status)]
      .filter((s): s is string => !!s)
      .join(' · ');
    return { id: l.id, name, sub, checked: this.checked.has(l.id), disabled: false, title: null };
  }

  private reasonsNote(count: number): string | null {
    const st = this.store.reasonsState;
    if (st === 'loading') return 'Chargement des motifs…';
    if (st === 'error') return 'Motifs indisponibles (c_values 42).';
    return count ? null : 'Aucun motif actif pour cet usage (c_values 42).';
  }

  private marksFor(checkedLines: RentalLineView[]): PickerMarks | null {
    if (!checkedLines.length) return null;
    const key = JSON.stringify([checkedLines.map((l) => l.id), this.edit, this.store.view?.computed_at]);
    if (key !== this.marksKey) {
      this.marksKey = key;
      this.marks = pickerMarksOf(checkedLines, { except: this.edit ?? [], kind: 'interruption' });
    }
    return this.marks;
  }

  // Entrée de même motif dont la plage (bornes incluses) a au moins un jour commun avec la saisie, portée exactement par les lignes cochées.
  private overlapOf(lines: RentalLineView[], checkedIds: number[]): OverlapVm | null {
    if (this.reasonId === null || this.start === undefined || !checkedIds.length) return null;
    const except = new Set((this.edit ?? []).map((e) => e.lineId + ':' + e.id));
    const checked = new Set(checkedIds);
    const first = lines.find((l) => l.id === checkedIds[0]);
    const lo = this.start ?? '';
    const hi = this.end ?? FAR;
    for (const i of first?.decisions?.interruptions ?? []) {
      if (i.cancelled || i.reason_id !== this.reasonId || except.has(first!.id + ':' + i.id)) continue;
      if (!((i.start ?? '') <= hi && lo <= (i.end ?? FAR))) continue;
      const key = sameDecisionKey(i);
      const entries: RangeEditTarget[] = [];
      for (const l of lines) {
        if (!selectable(l)) continue;
        const e = l.decisions!.interruptions.find((x) => !x.cancelled && !except.has(l.id + ':' + x.id) && sameDecisionKey(x) === key);
        if (e) entries.push({ lineId: l.id, id: e.id });
      }
      if (entries.length !== checked.size || !entries.every((e) => checked.has(e.lineId))) continue;
      const period = (i.start ? `du ${frShort(i.start)}` : 'dès la livraison') + (i.end ? ` au ${frShort(i.end)}` : ', sans reprise connue');
      return {
        text: `Chevauche « ${this.store.reasonName(i.reason_id)} » ${period} : les jours communs ne comptent qu'une fois.`,
        entries, sample: i, canExtend: !this.isEdit,
      };
    }
    return null;
  }

  // Remplacements, puis ajouts, puis annulations ; un évènement par ligne cochée, dans l'ordre des lignes.
  private buildEvents(): RentalDecisionEvent[] | null {
    const vm = this.vm;
    if (!vm.ready || !vm.simulable || this.reasonId === null || this.start === undefined) return null;
    const orderId = this.data.orderId;
    const ids = vm.checkedIds;
    const v = { start: this.start, end: this.end, reason_id: this.reasonId, comment: this.comment };
    const plan = this.editPlan(ids);
    if (!plan) return ids.map((id) => interruptionAdd(orderId, id, v));
    return [
      ...plan.replace.map((e) => interruptionReplace(orderId, e.lineId, e.id, this.editReason, v)),
      ...plan.add.map((id) => interruptionAdd(orderId, id, v)),
      ...plan.cancel.map((e) => decisionCancel(EventType.RENTAL_INTERRUPTION, orderId, e.lineId, e.id, this.editReason)),
    ];
  }

  // Modification d'interruption : une entrée inchangée n'est pas remplacée (§7.6 : « Appliquer à ce matériel » n'ajoute que la ligne manquante).
  private editPlan(ids: number[]): { replace: RangeEditTarget[]; add: number[]; cancel: RangeEditTarget[] } | null {
    if (!this.isEdit) return null;
    const o = this.original;
    const same = !!o && o.start === this.start && o.end === this.end && o.reason_id === this.reasonId && (o.comment ?? '').trim() === this.comment.trim();
    const carried = new Map((this.edit ?? []).map((e) => [e.lineId, e.id]));
    const seq = (lineId: number) => this.store.line(lineId)?.seq ?? lineId;
    return {
      replace: same ? [] : ids.filter((id) => carried.has(id)).map((id) => ({ lineId: id, id: carried.get(id)! })),
      add: ids.filter((id) => !carried.has(id)),
      cancel: [...carried.entries()]
        .filter(([lineId]) => !this.checked.has(lineId) && selectable(this.store.line(lineId))) // une ligne close garde son entrée
        .sort((a, b) => seq(a[0]) - seq(b[0]))
        .map(([lineId, id]) => ({ lineId, id })),
    };
  }

  private floor(): Day | null {
    return minDay(...this.store.rentalLines().filter((l) => this.checked.has(l.id)).map(deliveredDay));
  }

  private startEff(): Day | null {
    return this.start === undefined ? null : (this.start ?? this.floor());
  }

  private showMonthOf(d: Day | null): void {
    if (!d || !this.leftMonth) return;
    if (d < this.leftMonth || d >= addMonthsClamped(this.leftMonth, this.months)) this.leftMonth = firstOfMonth(d);
  }
}
